import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { useQuery, useLazyQuery } from "@apollo/client";
import { gql } from '@apollo/client';
import Loader from '../../../components/Loader';
import { useCartContext } from "../../../context/CartContext";
import { useNavigate } from 'react-router-dom';
import useResponsive from '../../../hooks/useResponsive';
import useSetOrderNote from '../../../hooks/useSetOrderNote';
import { T } from '../../../utils/I18n/I18n';
import readConfig from "../../../utils/readClientConfiguration";
import productionConfiguration from '../../../config/default.json';
import './KeksPay.scss';

const keksConfig = readConfig(productionConfiguration, 'keksPay');

const PREPARE_KEKS = gql`
    query prepareKeksPay {
        prepareKeksPay {
            active
            appstore_app_link
            appstore_logo_svg
            bill_id
            cid
            currency
            identity_hash
            is_sandbox
            kekspay_logo
            kekspay_logo_v
            order_id
            playstore_app_link
            playstore_logo_svg
            plus_icon
            qr_type
            store_description
            tid
        }
    }
`;

const RESET_CART = gql`
    query resetCart {
        resetCart
    }
`;

const POLL_ORDER = gql`
    query keksPayPollOrder($order_id: String!) {
        keksPayPollOrder(order_id: $order_id) {
            status
            message
            order_id
        }
    }
`;

const KeksPay = ({ closeModal }) => {
    const { isMobile } = useResponsive();
    const [resetCart] = useLazyQuery(RESET_CART);
    const { cart, refetchCart, orderNote, setOrderNote: clearNote } = useCartContext();
    const { setOrderNote } = useSetOrderNote();
    const navigate = useNavigate();

    const generateQRContent = ({ qr_type, bill_id, cid, tid, currency, store_description }) => {
        const amount = cart.prices.grand_total.value;
        return {
            qr_type,
            bill_id,
            cid,
            tid,
            amount,
            currency,
            store: store_description
        };
    };

    const generateMobileLink = ({ qr_type, bill_id, cid, tid, currency, is_sandbox, identity_hash }) => {
        const amount = cart.prices.grand_total.value;
        return {
            qr_type,
            bill_id,
            cid,
            tid,
            amount,
            currency,
            identity_hash,
            is_sandbox
        };
    };

    const [orderId, setOrderId] = useState(undefined);
    const [failed, setFailed] = useState(undefined);

    const handleRedirection = async (orderData) => {
        const { status } = orderData.keksPayPollOrder;

        switch (status) {
            case 0:
                await resetCart();
                await refetchCart();
                await clearNote("");
                navigate(`/checkout/success?identity_hash=${data?.prepareKeksPay?.identity_hash}`);
                break;
            case -6:
                setFailed(true);
                setTimeout(closeModal, 3000);
                break;
        }
    };

    // Poll the order and handle its output
    const [poll] = useLazyQuery(POLL_ORDER, {
        fetchPolicy: 'no-cache',
        onCompleted: handleRedirection
    });

    /**
     * Open the app and pay
     * @param config
     */
    const appPayment = (config) => {
        const currentUrl = window.location.origin;
        const route = config.is_sandbox ? `/${keksConfig.sandbox}` : `/${keksConfig.prod}/`;
        const openLink = `${keksConfig.baseUrl}${route}?cid=${config.cid}&qr_type=${config.qr_type}&tid=${config.tid}`
            + `&bill_id=${config.bill_id}¤cy=${config.currency}&amount=${config.amount}&success_url=${currentUrl}`
            + `/checkout/success?identity_hash=${config.identity_hash}`;
        window.open(openLink);
    };

    // Wait for changes made on the BE
    useEffect(() => {
        if (!orderId) return;
        const interval = setInterval(() => {
            poll({ variables: { order_id: orderId } });
        }, 1000);
        return () => clearInterval(interval);
    }, [orderId, poll]);

    // Set order note
    useEffect(() => {
        if (!cart || !cart.email || !cart.selected_payment_method || !cart.selected_payment_method.code || !orderNote) return;
        // Set the order note
        setOrderNote({
            note: orderNote,
            email: cart.email,
            method: cart.selected_payment_method.code
        });
    }, [orderNote, cart, setOrderNote]);

    const { data, loading } = useQuery(PREPARE_KEKS, {
        fetchPolicy: "no-cache"
    });


    if (loading || !data) {
        return (<div className="kekspay__background"><Loader /></div>);
    }

    const {
        appstore_app_link,
        playstore_app_link,
        order_id
    } = data.prepareKeksPay;

    // Set the orderId once we've loaded
    if (!orderId) {
        setOrderId(order_id);
    }

    return (
        <div className="kekspay__background">
            <div className="kekspay__modal">
                <div className="kekspay__modal__header">
                    <img className="kekspay__modal__logo" alt="kekspay-modal" src="/images/checkout/kekspay/kekspay_logo.svg" />
                </div>
                <div className="kekspay__modal__body">
                    {isMobile && (
                        <button
                            onClick={() => appPayment(generateMobileLink(data.prepareKeksPay))}
                            className="action primary checkout kekspay__cancel"
                        >
                            <T id="keksPay.openButton" />
                        </button>
                    )}
                    {!isMobile && (
                        <>
                            <p><T id="keksPay.open" /></p>
                            <p className="kekspay__modal__body__press-icon">
                                <T id="keksPay.pressIcon1" />
                                <img className="kekspay__plus-icon" alt="kekspay-icon" src="/images/checkout/kekspay/plus_icon.svg" />
                                <T id="keksPay.pressIcon2" />
                            </p>
                            <p><T id="keksPay.qrCodeSelect" /></p>
                            <p><T id="keksPay.qrCodeScan" /></p>
                            <QRCode
                                title="KeksPay"
                                size={180}
                                value={JSON.stringify(generateQRContent(data.prepareKeksPay))}
                            />
                        </>
                    )}
                    {failed ? <p className="kekspay__modal-error"><T id="keksPay.paymentFailed" /></p> : null}
                </div>
                <button className="action primary checkout kekspay__cancel" onClick={closeModal}>
                    <T id="keksPay.cancel" />
                </button>
                <div className='kekspay__downloads'>
                    <a href={appstore_app_link}>
                        <img className="kekspay__download-button" src="/images/checkout/kekspay/download_on_appstore.svg" alt="App Store" />
                    </a>
                    <a href={playstore_app_link}>
                        <img className="kekspay__download-button" src="/images/checkout/kekspay/download_on_playstore.svg" alt="Play Store" />
                    </a>
                </div>
            </div>
        </div>
    );
};

export default KeksPay;